// ─────────────────────────────────────────────────────────────
// sentry.config.ts
//
// Environment-derived Sentry configuration values.
// All three environments (testing / staging / production) share the
// SAME sampling and integrations. The ONLY value that differs by
// environment is `replayPrivacy` (masking).
// ─────────────────────────────────────────────────────────────

import { getDashboardEnvironment } from "../../utils/functions/client-code";

/** "production" | "staging" | "testing" | "development" (defaults to "production"). */
export const SENTRY_ENVIRONMENT = getDashboardEnvironment();

/** Production masks; every other environment shows non-sensitive data. */
export const IS_PRODUCTION = SENTRY_ENVIRONMENT === "production";

// ── Sampling — identical in every environment ──────────────────
// Replay is errors-only everywhere: a replay is recorded ONLY when an
// error occurs (onError 1.0), never for idle/error-free sessions
// (session 0). Keeps production under the Sentry free-tier replay quota.
export const TRACES_SAMPLE_RATE = 1.0;
export const REPLAYS_SESSION_SAMPLE_RATE = 0;
export const REPLAYS_ON_ERROR_SAMPLE_RATE = 1.0;

/**
 * Session Replay privacy — the ONE env-gated setting.
 * - production: mask typed inputs + any `.sentry-mask` element; page text /
 *   DOM / media otherwise visible.
 * - testing / staging / development: fully unmasked (non-sensitive data).
 * `<input type="password">` is always masked by Sentry regardless.
 */
export const replayPrivacy = IS_PRODUCTION
  ? {
      maskAllText: false,
      maskAllInputs: true,
      blockAllMedia: false,
      mask: [".sentry-mask"],
    }
  : {
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
    };

/**
 * Frontend-only tracing: attach trace headers to same-origin + localhost
 * requests only. The cross-origin vf-core API is intentionally excluded —
 * enabling it would require backend CORS to allow `sentry-trace` / `baggage`.
 */
export const TRACE_PROPAGATION_TARGETS: (string | RegExp)[] = [/^\//, "localhost"];

/** Benign browser noise we never want raised as Sentry issues. */
export const IGNORE_ERRORS: (string | RegExp)[] = [
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications.",
  // Amplitude ingest blocked client-side (ad blocker / VPN / DNS filtering),
  // not an app defect. Backstop — `AMPLITUDE_LOG_LEVEL` silences these at source.
  "Event rejected due to exceeded retry count",
  /Failed to fetch \(api2?\.amplitude\.com\)/,
];

/** Errors originating from browser extensions / non-app origins. */
export const DENY_URLS: (string | RegExp)[] = [
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
];
